import React, { useState } from 'react';
import { 
  X, Users, Calendar, CreditCard, ChevronRight, 
  Shield, Clock, DollarSign, Loader2, AlertCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ 
  isOpen, 
  onClose, 
  trip, 
  selectedDate, 
  language = 'en' 
}) => {
  const [step, setStep] = useState(1);
  const [travelers, setTravelers] = useState(1);
  const [paymentType, setPaymentType] = useState('deposit');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!trip) return null;

  const pricePerPerson = trip.price || 0;
  const totalAmount = pricePerPerson * travelers;
  const depositAmount = totalAmount * 0.05;
  const paymentAmount = paymentType === 'deposit' ? depositAmount : totalAmount;

  // Calculate balance due date (45 days before trip)
  const tripDate = selectedDate ? new Date(selectedDate) : null;
  const balanceDueDate = tripDate ? new Date(tripDate.getTime() - 45 * 24 * 60 * 60 * 1000) : null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSubmit = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      setError(language === 'es' ? 'Por favor complete todos los campos requeridos' : 'Please fill in all required fields');
      return;
    }

    if (!selectedDate) {
      setError(language === 'es' ? 'Por favor seleccione una fecha de viaje' : 'Please select a trip date');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/payments/checkout/create`, {
        trip_id: trip.id,
        trip_date: selectedDate,
        travelers: travelers,
        payment_type: paymentType,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone || null,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || (language === 'es' ? 'Error al procesar el pago' : 'Error processing payment'));
      setIsLoading(false);
    }
  };

  const texts = {
    en: {
      title: 'Book Your Adventure',
      step1: 'Trip Details',
      step2: 'Payment',
      step3: 'Checkout',
      travelers: 'Number of Travelers',
      tripDate: 'Trip Date',
      paymentOption: 'Payment Option',
      deposit: 'Pay 5% Deposit',
      depositDesc: 'Secure your spot now, pay the rest later',
      fullPayment: 'Pay in Full',
      fullPaymentDesc: 'Complete payment and skip the balance',
      yourInfo: 'Your Information',
      name: 'Full Name',
      email: 'Email',
      phone: 'Phone (optional)',
      summary: 'Booking Summary',
      pricePerPerson: 'Price per person',
      subtotal: 'Subtotal',
      dueNow: 'Due Now',
      balanceDue: 'Balance Due',
      balanceDate: 'Due by',
      continueBtn: 'Continue',
      backBtn: 'Back',
      proceedToPayment: 'Proceed to Payment',
      secureCheckout: 'Secure checkout powered by Stripe',
      spotsLeft: 'spots left',
      selectDate: 'Please select a date first'
    },
    es: {
      title: 'Reserva Tu Aventura',
      step1: 'Detalles del Viaje',
      step2: 'Pago',
      step3: 'Checkout',
      travelers: 'Numero de Viajeros',
      tripDate: 'Fecha del Viaje',
      paymentOption: 'Opcion de Pago',
      deposit: 'Pagar 5% de Deposito',
      depositDesc: 'Asegura tu lugar ahora, paga el resto despues',
      fullPayment: 'Pagar Completo',
      fullPaymentDesc: 'Completa el pago y evita el saldo',
      yourInfo: 'Tu Informacion',
      name: 'Nombre Completo',
      email: 'Correo',
      phone: 'Telefono (opcional)',
      summary: 'Resumen de Reserva',
      pricePerPerson: 'Precio por persona',
      subtotal: 'Subtotal',
      dueNow: 'A Pagar Ahora',
      balanceDue: 'Saldo Pendiente',
      balanceDate: 'Vence el',
      continueBtn: 'Continuar',
      backBtn: 'Atras',
      proceedToPayment: 'Proceder al Pago',
      secureCheckout: 'Pago seguro con Stripe',
      spotsLeft: 'lugares disponibles',
      selectDate: 'Por favor selecciona una fecha primero'
    }
  };

  const t = texts[language] || texts.en;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#84bc00] flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            {t.title}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 my-4">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  step >= s 
                    ? 'bg-[#84bc00] text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 rounded ${step > s ? 'bg-[#84bc00]' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Labels */}
        <div className="flex justify-between text-xs text-gray-500 mb-6 px-4">
          <span className={step >= 1 ? 'text-[#84bc00] font-bold' : ''}>{t.step1}</span>
          <span className={step >= 2 ? 'text-[#84bc00] font-bold' : ''}>{t.step2}</span>
          <span className={step >= 3 ? 'text-[#84bc00] font-bold' : ''}>{t.step3}</span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Step 1: Trip Details */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Trip Info Card */}
            <div className="bg-gray-50 rounded-xl p-4 flex gap-4">
              <img 
                src={trip.image} 
                alt={trip.name}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <div>
                <h3 className="font-bold text-lg text-gray-900">{trip.name}</h3>
                <p className="text-gray-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {trip.duration}
                </p>
                <p className="text-[#84bc00] font-bold text-lg mt-1">
                  ${pricePerPerson.toLocaleString()} / {language === 'es' ? 'persona' : 'person'}
                </p>
              </div>
            </div>

            {/* Selected Date */}
            <div>
              <Label className="font-bold flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" />
                {t.tripDate}
              </Label>
              {selectedDate ? (
                <div className="p-3 bg-[#84bc00]/10 border-2 border-[#84bc00] rounded-lg text-[#84bc00] font-medium">
                  {formatDate(selectedDate)}
                </div>
              ) : (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
                  {t.selectDate}
                </div>
              )}
            </div>

            {/* Number of Travelers */}
            <div>
              <Label className="font-bold flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" />
                {t.travelers}
              </Label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-10 h-10 p-0"
                  onClick={() => setTravelers(Math.max(1, travelers - 1))}
                  disabled={travelers <= 1}
                >
                  -
                </Button>
                <span className="text-2xl font-bold w-12 text-center">{travelers}</span>
                <Button
                  type="button"
                  variant="outline"
                  className="w-10 h-10 p-0"
                  onClick={() => setTravelers(Math.min(trip.spots || 10, travelers + 1))}
                  disabled={travelers >= (trip.spots || 10)}
                >
                  +
                </Button>
                <span className="text-sm text-gray-500">
                  ({trip.spots} {t.spotsLeft})
                </span>
              </div>
            </div>

            {/* Continue Button */}
            <Button
              className="w-full bg-[#84bc00] hover:bg-[#6a9600] py-6 text-lg font-bold"
              onClick={() => setStep(2)}
              disabled={!selectedDate}
              data-testid="booking-step1-continue"
            >
              {t.continueBtn}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Payment Option & Info */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Payment Options */}
            <div>
              <Label className="font-bold mb-3 block">{t.paymentOption}</Label>
              <RadioGroup value={paymentType} onValueChange={setPaymentType} className="space-y-3">
                <label 
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentType === 'deposit' 
                      ? 'border-[#84bc00] bg-[#84bc00]/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <RadioGroupItem value="deposit" id="deposit" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">{t.deposit}</span>
                      <span className="text-xl font-black text-[#84bc00]">
                        ${depositAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t.depositDesc}</p>
                  </div>
                </label>

                <label 
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentType === 'full' 
                      ? 'border-[#84bc00] bg-[#84bc00]/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <RadioGroupItem value="full" id="full" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">{t.fullPayment}</span>
                      <span className="text-xl font-black text-[#84bc00]">
                        ${totalAmount.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t.fullPaymentDesc}</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Customer Info */}
            <div className="space-y-4">
              <Label className="font-bold">{t.yourInfo}</Label>
              
              <div>
                <Label className="text-sm text-gray-600">{t.name} *</Label>
                <Input
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  placeholder="John Smith"
                  className="mt-1"
                  data-testid="booking-customer-name"
                />
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">{t.email} *</Label>
                <Input
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  placeholder="john@example.com"
                  className="mt-1"
                  data-testid="booking-customer-email"
                />
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">{t.phone}</Label>
                <Input
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="mt-1"
                  data-testid="booking-customer-phone"
                />
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 py-6"
                onClick={() => setStep(1)}
              >
                {t.backBtn}
              </Button>
              <Button
                className="flex-1 bg-[#84bc00] hover:bg-[#6a9600] py-6 font-bold"
                onClick={() => setStep(3)}
                disabled={!customerInfo.name || !customerInfo.email}
                data-testid="booking-step2-continue"
              >
                {t.continueBtn}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Summary & Checkout */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="font-bold text-lg mb-4">{t.summary}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{trip.name}</span>
                  <span className="font-medium">{formatDate(selectedDate)}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.pricePerPerson}</span>
                  <span className="font-medium">${pricePerPerson.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.travelers}</span>
                  <span className="font-medium">x {travelers}</span>
                </div>
                
                <hr className="my-2" />
                
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.subtotal}</span>
                  <span className="font-bold">${totalAmount.toLocaleString()}</span>
                </div>
                
                {paymentType === 'deposit' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t.balanceDue}</span>
                      <span className="font-medium">${(totalAmount - depositAmount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{t.balanceDate}</span>
                      <span>{formatDate(balanceDueDate?.toISOString().split('T')[0])}</span>
                    </div>
                  </>
                )}
                
                <hr className="my-2" />
                
                <div className="flex justify-between text-lg">
                  <span className="font-bold text-gray-900">{t.dueNow}</span>
                  <span className="font-black text-[#84bc00] text-xl">
                    ${paymentAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Info Summary */}
            <div className="bg-gray-50 rounded-xl p-5">
              <div className="text-sm space-y-1">
                <p><span className="text-gray-500">{t.name}:</span> {customerInfo.name}</p>
                <p><span className="text-gray-500">{t.email}:</span> {customerInfo.email}</p>
                {customerInfo.phone && (
                  <p><span className="text-gray-500">{t.phone}:</span> {customerInfo.phone}</p>
                )}
              </div>
            </div>

            {/* Security Note */}
            <div className="flex items-center gap-2 text-sm text-gray-500 justify-center">
              <Shield className="w-4 h-4" />
              {t.secureCheckout}
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 py-6"
                onClick={() => setStep(2)}
                disabled={isLoading}
              >
                {t.backBtn}
              </Button>
              <Button
                className="flex-1 bg-[#84bc00] hover:bg-[#6a9600] py-6 font-bold text-lg"
                onClick={handleSubmit}
                disabled={isLoading}
                data-testid="booking-proceed-payment"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {t.proceedToPayment}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
